"use client";

import {colors} from "@c10h/colors";
import SkipLink from "@components/SkipLink";
import memoizee from "memoizee";
import {useTranslation} from "@i18n/client";
import React, {memo, useEffect, useState} from "react";

import {Lookup} from "src/types";

import {useClientSideId} from "../../../hooks/useClientSideId";
import {AnalyticsEvent, analytics} from "../../../utils/analytics";
import {transparent} from "../../../utils/transparent";
import {supportLink} from "../../_common/_constants";
import {Btn} from "../V5Comp";
import QuestionAnswer from "./QuestionAnswer";
import SchemaHead from "./SchemaHead";
import {QA, SectionDivider, isSectionDivider} from "./types";

export type LocalQA = QA & {
  active?: boolean;
  hide?: boolean;
};

type Props = React.HTMLAttributes<HTMLDivElement> & {
  header?: string;
  faq: (QA | SectionDivider)[];
  supportClickEventData?: Partial<AnalyticsEvent>;
  skipLinkTargetId?: string;
  supportLink?: string;
};

const formatFaq = memoizee((faq: (QA | SectionDivider)[]) =>
  faq.map(qa =>
    isSectionDivider(qa)
      ? qa
      : {
          ...qa,
          tags: qa.tags || [],
          active: false,
        },
  ),
);

const Faq = ({
  faq,
  supportClickEventData = {},
  skipLinkTargetId = "explore-locations",
  className,
  header,
  supportLink: _supportLink = supportLink,
  ...props
}: Props): React.ReactElement => {
  const i18n = useTranslation();

  const [state, setState] = useState<(SectionDivider | LocalQA)[]>(formatFaq(faq));
  const [tagCounts, setTagCounts] = useState([]);
  const [tagFilters, setTagFilters] = useState([]);

  useEffect(() => {
    setState(formatFaq(faq));
  }, [faq]);

  const toggleByIndex = (index: number) => () => {
    const updatedFaq = state.map((val, i) => {
      if (i === index && !isSectionDivider(val)) {
        return {
          ...val,
          active: !val.active,
        };
      }
      return val;
    });
    setState(updatedFaq);
  };

  // @ts-expect-error TS7006: Parameter 'clickedTag' implicitly has an 'any' type.
  const toggleTagFilter = clickedTag => () => {
    // @ts-expect-error TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
    if (tagFilters.includes(clickedTag)) {
      setTagFilters(tagFilters.filter(tag => tag !== clickedTag));
    } else {
      // @ts-expect-error TS2322, TS2322: Type 'any' is not assignable to type 'never'.,  Type 'any' is not assignable to type 'never'.
      setTagFilters([...tagFilters, clickedTag]);
    }
  };

  const headingId = useClientSideId();

  useEffect(() => {
    const updatedFaq = state.map(val => {
      if (isSectionDivider(val)) return val;
      const matchesTagFilters = tagFilters.reduce(
        // @ts-expect-error TS2532: Object is possibly 'undefined'.
        (acc, next) => acc && val.tags.includes(next),
        true,
      );
      const shouldHide = !matchesTagFilters && !tagFilters.isEmpty();
      return Object.assign(val, {
        hide: shouldHide,
        active: val.active && !shouldHide,
      });
    });

    const tagsAndCounts = state
      .filter(qa => !isSectionDivider(qa) && !qa.hide)
      .reduce<Lookup<number>>((acc, curr) => {
        const nextVal = curr as LocalQA;
        nextVal.tags?.forEach(tag => {
          Object.assign(acc, {[tag]: (acc[tag] || 0) + 1});
        });
        return acc;
      }, {} as Lookup<number>);

    setState(updatedFaq);
    // @ts-expect-error TS2345: Argument of type '{ tag: string; count: number; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setTagCounts(tagsAndCounts.getKeys().map(key => ({tag: key, count: tagsAndCounts[key]})));
  }, [tagFilters]);

  useEffect(() => {
    const {hash} = window.location;
    if (hash) {
      const index = parseInt(hash.replace("#faq-", ""));
      if (Number.isInteger(index)) {
        toggleByIndex(index)();
      }
    }
  }, []);
  return (
    <section
      id="faq"
      className={`bg-white pt8 pb14 df fdc aifs ph8 ph4-md maxw80 ma ${className || ""}`}
      {...props}
      aria-labelledby={headingId}
    >
      {faq.length > 0 && <SchemaHead faq={faq.filter(qa => !isSectionDivider(qa)) as QA[]} />}
      <SkipLink item={{text: i18n.t("Skip FAQs"), targetId: skipLinkTargetId}} showOnFocus />
      <div className="pt5 w100p">
        <h2 className="mb4 fs38 fs24-xs" id={headingId}>
          {header || i18n.t("Questions about Carbon Health")}
        </h2>
        <div className="mb4">
          <Btn
            target="_blank"
            href={_supportLink}
            bg={transparent(colors.gray800, 10)}
            color={colors.gray800}
            className="fs14-f-sm"
            onClick={() => {
              analytics.post({
                action: analytics.action.CLICKED,
                category: analytics.category.SUPPORT,
                label: analytics.label.CONTACT_SUPPORT,
                value: "Go to Support Center",
                ...supportClickEventData,
              });
            }}
          >
            {i18n.t("Go to Support Center") /* IF CHANGED UPDATE EVENT DATA ABOVE */}
          </Btn>
        </div>
        {tagCounts.length > 0 && (
          <div className="df fw minh3rem">
            {tagCounts.map(item => (
              <button
                // @ts-expect-error TS2339: Property 'tag' does not exist on type 'never'.
                key={item.tag}
                // @ts-expect-error TS2339: Property 'tag' does not exist on type 'never'.
                onClick={toggleTagFilter(item.tag)}
                className={`t-200 focus-bsDarkBlue3 pos-r df cp aic p3 m1 brd1nc br5 font-im fs14 brd-gray100-f hover-bg-lightYellow hoverbrd-lightYellow-f ${
                  // @ts-expect-error TS2345, TS2339: Argument of type 'any' is not assignable to parameter of type 'never'.,  Property 'tag' does not exist on type 'never'.
                  tagFilters.includes(item.tag) ? "bg-lightYellow brd-lightYellow-f" : ""
                }`}
              >
                {/* @ts-expect-error TS2339: Property 'tag' does not exist on type 'never'. */}
                <span className="mr4">{item.tag}</span>
                {/* @ts-expect-error TS2339: Property 'count' does not exist on type 'never'. */}
                <span className="gray400 pos-a" style={{right: item.count > 9 ? 10 : 12}}>
                  {/* @ts-expect-error TS2339: Property 'count' does not exist on type 'never'. */}
                  {item.count}
                </span>
              </button>
            ))}
          </div>
        )}
        <dl>
          {state.map((item, i, arr) => {
            if (isSectionDivider(item)) {
              const remainingItems = arr.slice(i + 1).filter(v => isSectionDivider(v) || !v.hide);
              if (!remainingItems[0] || isSectionDivider(remainingItems[0])) {
                return null;
              }
              return (
                <div
                  key={item.title}
                  className="font-cm gray400 fs20 mt14 mb4 ml4-sm"
                  style={{letterSpacing: -0.3}}
                >
                  {item.title}
                </div>
              );
            }
            return (
              <QuestionAnswer
                key={item.q}
                q={item.q}
                a={item.a}
                tags={item.tags}
                hideBorder={i === state.length - 1}
                linkColorBlue
              />
            );
          })}
        </dl>
      </div>
    </section>
  );
};

export default memo(Faq);
